import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Layers } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

import { formatDate } from "../../../../components/design-system/admin";
import { Space } from "../../../vendor/my-spaces/models/space.model";
import { useSpacesApi } from "../../../../common/hooks/use-spaces-api.hook";
import { surfaceLabel } from "../spaces.utils";

const DetailRow = ({ label, value }: { label: string; value: ReactNode }) => (
    <div className="flex items-baseline justify-between gap-4 border-b py-2 last:border-b-0">
        <dt className="text-sm text-muted-foreground">{label}</dt>
        <dd className="text-end text-sm tabular-nums">{value}</dd>
    </div>
);

/**
 * One space, fetched fresh (GET /spaces/:id): the photo, its size and surfaces.
 * Mounted while an id is chosen; closing it clears the id.
 */
export const SpaceView = ({
    selectedSpaceForViewID,
    setSelectedSpaceForView,
    onOpenInManager,
}: {
    selectedSpaceForViewID: string;
    setSelectedSpaceForView: (id: string | null) => void;
    onOpenInManager?: (space: Space) => void;
}) => {
    const { t } = useTranslation();
    const [space, setSpace] = useState<Space>();
    const [failed, setFailed] = useState(false);
    const spacesAPI = useSpacesApi();

    useEffect(() => {
        const getSpace = async () => {
            try {
                const result = await spacesAPI.findOneSpace(selectedSpaceForViewID);
                setSpace(result);
            } catch (error) {
                console.error("Error loading space:", error);
                setFailed(true);
            }
        };
        getSpace();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSpaceForViewID]);

    const close = () => setSelectedSpaceForView(null);

    return (
        <Dialog open onOpenChange={(open) => !open && close()}>
            <DialogContent className="max-h-[90vh] overflow-auto sm:max-w-4xl" data-testid="space-view">
                <DialogHeader>
                    <DialogTitle className="font-heading text-lg font-medium tracking-tight">
                        {space ? space.name || t("ADMIN_SPACES.UNNAMED") : failed ? t("ADMIN_SHELL.COMMON.LOAD_FAILED") : t("ADMIN_SHELL.COMMON.LOADING")}
                    </DialogTitle>
                </DialogHeader>

                {space ? (
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-5">
                        <div className="overflow-hidden rounded-lg bg-muted md:col-span-3">
                            {space.thumbnailPath && <img src={space.thumbnailPath} alt="" className="block w-full" />}
                        </div>
                        <dl className="md:col-span-2">
                            {space.spaceType && <DetailRow label={t("ADMIN_SPACES.DETAILS.ROOM_TYPE")} value={space.spaceType} />}
                            <DetailRow label={t("ADMIN_SPACES.DETAILS.HEIGHT")} value={t("ADMIN_SPACES.VALUE_CM", { value: space.height })} />
                            <DetailRow label={t("ADMIN_SPACES.DETAILS.WIDTH")} value={t("ADMIN_SPACES.VALUE_CM", { value: space.width })} />
                            <DetailRow
                                label={t("ADMIN_SPACES.DETAILS.SURFACES")}
                                value={(space.surfaces ?? []).map((s) => surfaceLabel(t, s)).join(", ") || "—"}
                            />
                            {space.createdAt && <DetailRow label={t("ADMIN_SPACES.DETAILS.ADDED")} value={formatDate(space.createdAt)} />}
                        </dl>
                    </div>
                ) : !failed ? (
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-5">
                        <Skeleton className="aspect-4/3 rounded-lg md:col-span-3" />
                        <div className="flex flex-col gap-3 md:col-span-2">
                            <Skeleton className="h-5" />
                            <Skeleton className="h-5" />
                            <Skeleton className="h-5" />
                        </div>
                    </div>
                ) : null}

                {space && onOpenInManager && (
                    <DialogFooter>
                        <Button variant="outline" onClick={() => onOpenInManager(space)}>
                            <Layers />
                            {t("ADMIN_SPACES.OPEN_IN_MANAGER")}
                        </Button>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
};
